import React, { useState } from 'react';
import { Candidate } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useUpdateCandidateProfile } from "@/hooks/useUpdateCandidateProfile";
import { 
  Target, 
  MapPin, 
  DollarSign,
  Building,
  Briefcase,
  Clock,
  Edit3,
  Save,
  X,
  Plus
} from 'lucide-react';

interface JobPreferencesProps {
  candidate: Candidate;
}

export const JobPreferences: React.FC<JobPreferencesProps> = ({ candidate }) => {
  const { updateCandidateProfile, isUpdatingProfile } = useUpdateCandidateProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [preferences, setPreferences] = useState({
    // From existing candidate fields
    remote_ok: candidate.remote_ok || false,
    location_city: candidate.location_city || '',
    location_state: candidate.location_state || '',
    salary_amount: candidate.salary_amount || null,
    salary_currency: candidate.salary_currency || 'USD',
    work_type: candidate.work_type || '',
    
    // Extended preferences (would need database fields)
    desired_roles: candidate.skills || [], // Using skills as proxy for now
    preferred_industries: ['Technology', 'Software'], // Mock data
    career_goals: candidate.notes || '', // Using notes as proxy
    availability_status: 'available', // Mock data
  });

  const [newRole, setNewRole] = useState('');
  const [newIndustry, setNewIndustry] = useState('');

  const handleSave = () => {
    updateCandidateProfile({
      candidateId: candidate.id,
      data: {
        remote_ok: preferences.remote_ok,
        location_city: preferences.location_city,
        location_state: preferences.location_state,
        salary_amount: preferences.salary_amount,
        salary_currency: preferences.salary_currency,
        work_type: preferences.work_type,
        notes: preferences.career_goals, // Map to existing field for now
      }
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    // Reset to original values
    setPreferences({
      remote_ok: candidate.remote_ok || false,
      location_city: candidate.location_city || '',
      location_state: candidate.location_state || '',
      salary_amount: candidate.salary_amount || null,
      salary_currency: candidate.salary_currency || 'USD',
      work_type: candidate.work_type || '',
      desired_roles: candidate.skills || [],
      preferred_industries: ['Technology', 'Software'],
      career_goals: candidate.notes || '',
      availability_status: 'available',
    });
    setIsEditing(false);
  };

  const addDesiredRole = () => {
    if (newRole.trim()) {
      setPreferences(prev => ({
        ...prev,
        desired_roles: [...prev.desired_roles, newRole.trim()]
      }));
      setNewRole('');
    }
  };

  const removeDesiredRole = (roleToRemove: string) => {
    setPreferences(prev => ({
      ...prev,
      desired_roles: prev.desired_roles.filter(role => role !== roleToRemove)
    }));
  };

  const addPreferredIndustry = () => {
    if (newIndustry.trim()) {
      setPreferences(prev => ({
        ...prev,
        preferred_industries: [...prev.preferred_industries, newIndustry.trim()]
      }));
      setNewIndustry('');
    }
  };

  const removePreferredIndustry = (industryToRemove: string) => {
    setPreferences(prev => ({
      ...prev,
      preferred_industries: prev.preferred_industries.filter(industry => industry !== industryToRemove)
    }));
  };

  const formatSalary = (amount: number | null, currency: string) => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getAvailabilityBadgeVariant = (status: string) => {
    switch (status) {
      case 'available': return 'default';
      case 'actively_looking': return 'secondary';
      case 'open_to_opportunities': return 'outline';
      case 'not_available': return 'destructive';
      default: return 'outline';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Job Preferences
          </CardTitle>
          {!isEditing ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-1"
            >
              <Edit3 className="h-3 w-3" />
              Edit Preferences
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="flex items-center gap-1"
              >
                <X className="h-3 w-3" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                className="flex items-center gap-1"
              >
                <Save className="h-3 w-3" />
                Save
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Availability Status */}
        <div>
          <Label className="text-sm font-medium mb-2 flex items-center gap-1">
            <Clock className="h-4 w-4" />
            Availability Status
          </Label>
          <Badge variant={getAvailabilityBadgeVariant(preferences.availability_status)}>
            {preferences.availability_status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </Badge>
        </div>

        <Separator />

        {/* Desired Roles */}
        <div>
          <Label className="text-sm font-medium mb-2 flex items-center gap-1">
            <Briefcase className="h-4 w-4" />
            Desired Job Titles/Roles
          </Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {preferences.desired_roles.map((role, index) => (
              <Badge key={index} variant="secondary" className="flex items-center gap-1">
                {role}
                {isEditing && (
                  <X 
                    className="h-3 w-3 cursor-pointer hover:text-destructive" 
                    onClick={() => removeDesiredRole(role)}
                  />
                )}
              </Badge>
            ))}
          </div>
          {isEditing && (
            <div className="flex gap-2">
              <Input
                placeholder="Add desired role..."
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addDesiredRole()}
                className="flex-1"
              />
              <Button size="sm" onClick={addDesiredRole}>
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>

        {/* Preferred Industries */}
        <div>
          <Label className="text-sm font-medium mb-2 flex items-center gap-1">
            <Building className="h-4 w-4" />
            Preferred Industries
          </Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {preferences.preferred_industries.map((industry, index) => (
              <Badge key={index} variant="outline" className="flex items-center gap-1">
                {industry}
                {isEditing && (
                  <X 
                    className="h-3 w-3 cursor-pointer hover:text-destructive" 
                    onClick={() => removePreferredIndustry(industry)}
                  />
                )}
              </Badge>
            ))}
          </div>
          {isEditing && (
            <div className="flex gap-2">
              <Input
                placeholder="Add preferred industry..."
                value={newIndustry}
                onChange={(e) => setNewIndustry(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addPreferredIndustry()}
                className="flex-1"
              />
              <Button size="sm" onClick={addPreferredIndustry}>
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>

        <Separator />

        {/* Salary Expectations */}
        <div>
          <Label className="text-sm font-medium mb-2 flex items-center gap-1">
            <DollarSign className="h-4 w-4" />
            Salary Expectations
          </Label>
          {isEditing ? (
            <div className="grid grid-cols-3 gap-2">
              <Input
                type="number"
                placeholder="Amount"
                value={preferences.salary_amount || ''}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  salary_amount: e.target.value ? Number(e.target.value) : null
                }))}
                className="col-span-2"
              />
              <select
                value={preferences.salary_currency}
                onChange={(e) => setPreferences(prev => ({ ...prev, salary_currency: e.target.value }))}
                className="px-3 py-2 border rounded-md bg-background text-sm"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
              </select>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              {formatSalary(preferences.salary_amount, preferences.salary_currency)}
            </div>
          )}
        </div>

        {/* Location Preferences */}
        <div>
          <Label className="text-sm font-medium mb-2 flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            Location Preferences
          </Label>
          {isEditing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="City"
                  value={preferences.location_city}
                  onChange={(e) => setPreferences(prev => ({ ...prev, location_city: e.target.value }))}
                />
                <Input
                  placeholder="State/Province"
                  value={preferences.location_state}
                  onChange={(e) => setPreferences(prev => ({ ...prev, location_state: e.target.value }))}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="remote-work"
                  checked={preferences.remote_ok}
                  onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, remote_ok: checked }))}
                />
                <Label htmlFor="remote-work" className="text-sm">
                  Open to remote work
                </Label>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">
                {preferences.location_city || preferences.location_state 
                  ? `${preferences.location_city}${preferences.location_city && preferences.location_state ? ', ' : ''}${preferences.location_state}`
                  : 'No location preference specified'
                }
              </div>
              {preferences.remote_ok && (
                <Badge variant="outline" className="text-xs">
                  Open to Remote Work
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Work Type */}
        <div>
          <Label className="text-sm font-medium mb-2">Employment Type Preference</Label>
          {isEditing ? (
            <select
              value={preferences.work_type}
              onChange={(e) => setPreferences(prev => ({ ...prev, work_type: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md bg-background text-sm"
            >
              <option value="">No preference</option>
              <option value="full-time">Full-time</option>
              <option value="part-time">Part-time</option>
              <option value="contract">Contract</option>
              <option value="freelance">Freelance</option>
              <option value="temporary">Temporary</option>
            </select>
          ) : (
            <div className="text-sm text-muted-foreground">
              {preferences.work_type || 'No preference specified'}
            </div>
          )}
        </div>

        <Separator />

        {/* Career Goals */}
        <div>
          <Label className="text-sm font-medium mb-2">Career Goals & Notes</Label>
          {isEditing ? (
            <Textarea
              placeholder="Describe career goals, preferences, or any additional notes..."
              value={preferences.career_goals}
              onChange={(e) => setPreferences(prev => ({ ...prev, career_goals: e.target.value }))}
              rows={3}
            />
          ) : (
            <div className="text-sm text-muted-foreground">
              {preferences.career_goals || 'No career goals specified'}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};